import { Box, Switch } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  useGridApiRef,
} from "@mui/x-data-grid";
import { ActivityTemplatesContext } from "../../../contexts/ActivityTemplatesContext";
import { Trans, useTranslation } from "react-i18next";
import alertService from "../../../components/alert/alertService";
import { TemplateActivities } from "../../../models/TemplateActivities";
import TemplateActivitiesService from "../../../service/template-activities.service";
import { useEffect, useState } from "react";
import { EditActivityTemplates } from "./EditActivityTemplates";
import { QuickSearchToolbar } from "./QuickSearchToolbar";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { DROPDOWN_EMISSION_GROUP } from "../../../components/emission/emission-components";
import i18n, { common, input } from "../../../i18n";
import dayjs, { Dayjs } from "dayjs";
import TableApprovalStatus from "../../emission/_components/data-table/TableApprovalStatus";
import { tableStyle } from "../../emission/_components/data-table/EmissionDataTableStyle";

export const ActivityTemplatesTable = () => {
  const apiRef = useGridApiRef();

  // ✅ เพิ่ม State ควบคุม Modal และข้อมูล Row ที่จะ Edit
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TemplateActivities | null>(
    null,
  );

  const {
    templateActivitiesList,
    transformersToDataGrid,
    getTemplateActivities,
  } = ActivityTemplatesContext();

  const { t } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const columns: GridColDef<(typeof templateActivitiesList)[number]>[] = [
    {
      field: "id",
      headerName: t("ID"),
      disableColumnMenu: true,
      minWidth: 80,
      type: "string",
      headerAlign: "left",
      align: "left",
      valueFormatter: (value, row) => `AT-${value}`,
    },
    {
      field: "isMonthly",
      headerName: t("Monthly"),
      disableColumnMenu: true,
      width: 120,
      type: "boolean",
      headerAlign: "left",
      align: "center",
      renderCell: (params) => {
        return (
          <Switch
            id={"switch" + params.row._id}
            key={"switch" + params.row._id}
            checked={params.value}
            onChange={() => updateIsMonthly(params.row)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      field: "year",
      headerName: t("Year"),
      disableColumnMenu: true,
      type: "string",
      headerAlign: "left",
      align: "left",
    },
    {
      field: "suborganizationName",
      headerName: t("Sub-Organizations"),
      headerAlign: "left",
      minWidth: 138,
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: "siteName",
      headerName: t("Site"),
      headerAlign: "left",
      minWidth: 138,
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: "assetName",
      headerName: t("Asset"),
      minWidth: 112,
      headerAlign: "left",
      flex: 1,
      disableColumnMenu: true,
      getApplyQuickFilterFn: undefined,
    },
    {
      field: "emissionGroup",
      headerName: t("Emission-Group"),
      minWidth: 136,
      flex: 1,
      headerAlign: "left",
      disableColumnMenu: true,
      valueFormatter: (value) => {
        const item = DROPDOWN_EMISSION_GROUP.find(
          (e) => e.emissionGroup === value,
        );

        return item ? t(item.groupEn) : t(value);
      },
    },
    {
      field: "activityAttributeEN",
      headerName: t("Emission-Source"),
      minWidth: 156,
      flex: 1,
      headerAlign: "left",
      disableColumnMenu: true,
      valueFormatter: (value, row) =>
        i18n.language === "en"
          ? row.activityAttributeEN
          : row.activityAttributeTh,
    },
    {
      field: "unit",
      headerName: t("Unit"),
      sortable: false,
      headerAlign: "left",
      disableColumnMenu: true,
    },
    {
      field: "lastGeneratedAt",
      headerName: t("Status-Activity-Templates"),
      sortable: false,
      headerAlign: "center",
      disableColumnMenu: true,
      minWidth: 140,
      type: "boolean",
      valueFormatter: (value) => (value ? true : false),
      renderCell: (params) => {
        const date: Dayjs | undefined = params.value
          ? dayjs(params.value)
          : undefined;

        return (
          <TableApprovalStatus
            by={
              params.row.isMonthly === true
                ? date
                  ? date.format("DD/MM/YYYY")
                  : ""
                : ""
            }
            status={
              params.row.isMonthly === true
                ? params.formattedValue
                  ? "generated"
                  : "ungenerated"
                : "-"
            }
          />
        );
      },
    },
    {
      field: "remark",
      headerName: t("Remark"),
      headerAlign: "left",
      minWidth: 100,
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: "",
      headerName: "",
      disableColumnMenu: true,
      sortable: false,
      type: "actions",
      width: 50,
      getApplyQuickFilterFn: undefined,
      getActions: (params) => {
        return [
          <GridActionsCellItem
            key={"delete-" + params.id}
            id={"delete-" + params.id}
            icon={
              <img
                src="/img/delete.svg"
                alt="icon-delete"
                height={"18px"}
                width={"18px"}
              />
            }
            label="delete"
            onClick={(e) => {
              e.stopPropagation(); // ✅ ป้องกันการกดปุ่มลบ แล้วทำให้แถวเปิด Modal เด้งขึ้นมา
              deleteTemplateActivities(params.row);
            }}
          />,
        ];
      },
    },
  ];

  const updateIsMonthly = async (row: TemplateActivities) => {
    const confirm = await alertService.confirm(
      t("Edit"),
      <Trans
        t={t}
        i18nKey="confirm-update-monthly-status"
        values={{ status: row.isMonthly ? t("off") : t("on"), id: row.id }}
        components={{
          span: <span style={{ color: "var(--red)" }} />,
        }}
      />,
    );

    if (confirm) {
      let result;
      try {
        alertService.showLoading();
        result =
          await TemplateActivitiesService.updateIsMonthlyTemplateActivity(
            row._id,
            !row.isMonthly,
          );
      } catch {
        // error alert already shown by service
        return;
      } finally {
        alertService.closeLoading();
      }

      if (result) {
        alertService.mixin(t("Successfully"));
        transformersToDataGrid(result);
      }
    }
  };

  const deleteTemplateActivities = async (row: TemplateActivities) => {
    const confirm = await alertService.confirm(
      t("Delete"),
      <Trans
        t={t}
        i18nKey="delete-templates-model-text"
        values={{ id: row.id }}
        components={{
          span: <span style={{ color: "var(--red)" }} />,
        }}
      />,
    );

    if (confirm) {
      let result;
      try {
        alertService.showLoading();
        result = await TemplateActivitiesService.deleteTemplateActivity(
          row._id,
        );
      } catch {
        // error alert already shown by service
        return;
      } finally {
        alertService.closeLoading();
      }

      if (result) {
        transformersToDataGrid(result);
        alertService.mixin(t("Successfully"));
      }
    }
  };

  useEffect(() => {
    getTemplateActivities();
  }, []);

  return (
    <>
      <Box
        width={"100%"}
        maxHeight={{ xs: "auto", md: window.innerHeight - 53 }}
      >
        <AppDataGrid
          rows={templateActivitiesList}
          columns={columns}
          rowHeight={60}
          apiRef={apiRef}
          // ✅ เมื่อคลิกที่แถว ให้เซ็ตค่า row และเปิด Modal
          onRowClick={(params) => {
            setSelectedRow(params.row);
            setEditModalOpen(true);
          }}
          slots={{ toolbar: QuickSearchToolbar }}
          localeText={{
            toolbarExport: t("Export"),
            toolbarQuickFilterPlaceholder: `${t("search")}...`,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                variant: "outlined",
              },
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 50 },
            },
            filter: {
              filterModel: {
                items: [],
              },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          sx={{
            ...tableStyle(),
          }}
        />
      </Box>

      {selectedRow && (
        <EditActivityTemplates
          row={selectedRow}
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setTimeout(() => setSelectedRow(null), 300);
          }}
        />
      )}
    </>
  );
};
